<template>
  <div class="fts-builder fts-client">
    <template v-if="isReady">
      <!-- sidenav -->
      <div class="client-sidenav" :class="{'client-sidenav--hidden': deviceFullView}">
        <div class="client-sidenav-header">
          <div class="client-sidenav-wrapper">
            <img :src="appLogo" alt="app-logo" class="client-sidenav-logo" />
          </div>
        </div>

        <div class="client-sidenav-body fts-scrollbar fts-scrollbar-overlay">
          <!-- dashboard -->
          <fts-tippy
            triggerClass="sidenav-menu-outer"
            content="Dashboard"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
          >
            <div
              slot="trigger"
              class="sidenav-menu-wrapper"
              :class="{'active': page === 'dashboard'}"
              @click="selectPage('dashboard')"
            >
              <font-awesome-icon
                :icon="['fas', 'chart-column']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </div>
          </fts-tippy>
          <!-- notification status -->
          <fts-tippy
            trigger-class="sidenav-menu-outer"
            content="Notification status"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
          >
             <div
              slot="trigger"
              class="sidenav-menu-wrapper"
              :class="{'active': page === 'notificationPanel'}"
              @click="selectPage('notificationPanel')"
            >
              <font-awesome-icon
                :icon="['far', 'bell']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </div>
          </fts-tippy>
          <!-- design -->
          <fts-tippy
            trigger-class="sidenav-menu-outer"
            content="Design"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
          >
             <div
              slot="trigger"
              class="sidenav-menu-wrapper"
              :class="{'active': ['app', 'web'].indexOf(page) > -1}"
              @click="selectPage(selectedDevice || 'App')"
            >
              <font-awesome-icon 
                :icon="['fas', 'wand-magic-sparkles']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </div>
          </fts-tippy>
          <!-- users -->
          <fts-tippy
            triggerClass="sidenav-menu-outer"
            content="Users"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
          >
            <router-link
              slot="trigger"
              :to="{ name: 'users' }"
              class="sidenav-menu-wrapper"
              data-v-step="studio-tour-manage-user"
            >
              <font-awesome-icon
                :icon="['fas', 'user-friends']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </router-link>
          </fts-tippy>
          <!-- invitation -->
          <fts-tippy
            triggerClass="sidenav-menu-outer"
            content="Invite"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
          >
            <div
              slot="trigger"
              class="sidenav-menu-wrapper"
              @click="openInvitation"
              data-v-step="studio-tour-invite-user"
            >
              <font-awesome-icon
                :icon="['fas', 'user-plus']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </div>
          </fts-tippy>
        </div>

        <div class="client-sidenav-footer">
          <fts-tippy
            trigger-class="sidenav-menu-outer"
            content="Help"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
          >
            <a
              slot="trigger"
              :href="discordURL"
              target="_blank"
              class="sidenav-menu-wrapper"
            >
              <font-awesome-icon
                :icon="['far', 'question-circle']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </a>
          </fts-tippy>
          
          <fts-tippy
            trigger-class="sidenav-menu-outer"
            content="Have any questions? Click and join our Discord support"
            :options="{ placement: 'left' }"
            class="sidenav-menu"
          >
            <a
              slot="trigger"
              :href="discordURL"
              target="_blank"
              class="sidenav-menu-wrapper"
            >
              <font-awesome-icon
                :icon="['fab', 'discord']"
                fixed-width
                size="lg"
                class="sidenav-icon"
              />
            </a>
          </fts-tippy>

          <fts-profile
            :profile="userProfile"
            :profile-photo-size="30"
            :profile-name="false"
            profile-photo-class="user-profile-avatar"
            class="client-user-profile"
            @click.native="openUserProfileModal"
          />
        </div>
      </div>
      <!-- builder -->
      <div class="builder-container">
        <div class="builder-header" :class="{'builder-header--hidden': deviceFullView}">
          <div class="builder-header__l">
            <fts-dropdown-select
              append-content-class="dropdown-menu"
              @show="getWorkspaceOptions"
              @selected="selectWorkspaceOption"
            >
              <template #toggle>
                <div class="builder-header-workspace">
                  <div class="builder-header-workspace-label">{{workspace.name || "Workspace"}}</div>
                  <font-awesome-icon :icon="['fas', 'angle-down']" class="builder-header-workspace-caret" />
                </div>
              </template>
              <template #menu="{ select }">
                <div class="dropdown-filter">
                  <div class="fts-form-control-filter">
                    <input
                      type="text"
                      class="fts-form-control"
                      v-model.trim="workspaceSearch"
                      placeholder="Search workspace"
                      @input="onSearchWorkspace"
                      ref="workspaceSearch"
                    />
                    <div class="search-icon">
                      <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                    </div>
                  </div>
                </div>

                <template v-if="workspaceOptions && workspaceOptions.length">
                  <div
                    v-for="workspace in workspaceOptions"
                    :key="workspace.id"
                    class="dropdown-option"
                    @click="select(workspace)"
                  >
                    <div>{{workspace.name}}</div>
                  </div>
                </template>

                <div class="dropdown-info" v-else>
                  <div class="dropdown-info-icon">
                    <font-awesome-icon :icon="['fas', 'search']" />
                  </div>
                  <div class="dropdown-info-label">No matching results</div>
                </div>

                <div class="dropdown-divider"></div>

                <div class="dropdown-option" @click="select('edit')">
                  <div class="dropdown-option-icon">
                    <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
                  </div>
                  <div>Manage workspace</div>
                </div>

                <div class="dropdown-option" @click="select('create')">
                  <div class="dropdown-option-icon">
                    <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
                  </div>
                  <div>Create a new workspace</div>
                </div>
              </template>
            </fts-dropdown-select>

            <div class="builder-header-status" v-if="submitting">
              <fts-spinner :size="14" />
              <div class="builder-header-status-label">saving...</div>
            </div>

            <template v-else>
              <div class="builder-header-status" v-if="submitResponse && submitResponse.isError">
                <font-awesome-icon :icon="['far', 'circle-xmark']" class="fts-text-danger" />
                <fts-tippy content="click to retry saving" class="builder-header-status-label">
                  <div class="fts-link-default" @click="executeSave">failed to save</div>
                </fts-tippy>
              </div>

              <fts-tippy
                :content="workspace && workspace.lastUpdatedOn ? `all changes saved on ${moment(workspace.lastUpdatedOn).format('lll')}` : 'all changes saved'"
                append-trigger-class="builder-header-status"
                v-else
              >
                <font-awesome-icon :icon="['far', 'circle-check']" class="fts-text-success" />
              </fts-tippy>
            </template>
          </div>
          <div class="builder-header__c">
            <div class="builder-header-title">Builder</div>
          </div>
          <div class="builder-header__r">
            <router-link to="/studio" custom v-slot="{ navigate }">
              <fts-tippy content="Switch to studio">
                <fts-btn
                  text
                  text-transform-init
                  box-shadow-none
                  @click.native="navigate"
                >
                  <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" /> Studio
                </fts-btn>
              </fts-tippy>
            </router-link>

            <fts-tippy content="Run app">
              <fts-btn
                text
                text-transform-init
                color="secondary" 
                @click="runApp"
              >
                <font-awesome-icon :icon="['fas', 'play']" /> Run
              </fts-btn>
            </fts-tippy>
          </div>
        </div>

        <div class="builder-body">
          <div class="builder-content">
            <div
              class="builder-content-control"
              :class="[{'builder-content-control--fab': page === 'app'}]"
              v-if="page === 'app' || page === 'web'"
            >
              <div class="builder-content-control__r">
                <fts-tippy content="Workflow">
                  <fts-btn
                    icon
                    text
                    color="var(--fts-default)"
                    @click="openWorkflow"
                    v-if="module.id"
                  >
                    <font-awesome-icon :icon="['fas', 'project-diagram']" />
                  </fts-btn>
                </fts-tippy>

                <fts-tippy content="Mobile preview">
                  <fts-btn
                    icon
                    text
                    :color="page === 'app' ? 'var(--fts-text)' : 'var(--fts-default)'"
                    @click="selectPage('app')"
                  >
                    <font-awesome-icon :icon="['fas', 'mobile-alt']" />
                  </fts-btn>
                </fts-tippy>

                <fts-tippy content="Desktop preview">
                  <fts-btn
                    icon
                    text
                    :color="page === 'web' ? 'var(--fts-text)' : 'var(--fts-default)'"
                    @click="selectPage('web')"
                  >
                    <font-awesome-icon :icon="['fas', 'desktop']" />
                  </fts-btn>
                </fts-tippy>

                <fts-tippy content="Reload">
                  <fts-btn
                    icon
                    text
                    @click="refreshPage"
                  >
                    <font-awesome-icon :icon="['fas', 'sync-alt']" />
                  </fts-btn>
                </fts-tippy>

                <fts-btn
                  icon
                  text
                  @click="deviceFullView = !deviceFullView"
                >
                  <font-awesome-icon :icon="['fas', 'times']" v-if="deviceFullView" />
                  <font-awesome-icon :icon="['fas', 'expand-alt']" v-else />
                </fts-btn>
              </div>

              <div class="builder-content-control__c" v-if="page === 'web'">
                <div class="builder-content-control-url">{{getDeviceWebUrl()}}</div>
              </div>
              <div class="builder-content-control__l" v-if="page !== 'dashboard'">
                <fts-run-as
                  :workspace-id="workspace.id"
                  :workspace-modules="workspaceModules"
                  :run-as.sync="isRunAs"
                  @selected="setDeviceRunAs"
                  v-if="isAdminUser"
                />
              </div>
            </div>

            <fts-dashboard
              :builder="false"
              ref="ftsDashboard"
              class="builder-content-dashboard fts-scrollbar"
              @module:select="selectPage('app')"
              v-if="page === 'dashboard'"
            />

            <notification-control-panel
              enable-module-select
              class="builder-content-notification-panel fts-scrollbar"
              v-else-if="page === 'notificationPanel'"
            />

            <template v-else>
              <div class="builder-content-playground" v-show="page === 'app' || page === 'web'">
                <fts-builder-device
                  :device="page"
                  :web-url="deviceWebUrl"
                  :app-url="deviceAppUrl"
                  ref="ftsBuilderDevice"
                />
              </div>
            </template>
          </div>

          <div
            class="builder-drawer-r"
            :class="{'builder-drawer-r--hidden': deviceFullView}"
            v-if="['app', 'web'].indexOf(page) > -1"
          >
            <div
              class="builder-drawer__header builder-drawer__header-module"
              :class="[
                {'builder-drawer__header-border-b': !module.id},
                {'builder-drawer__header-module--clickable': module.id && !isModuleForm}
              ]"
              @click="evt => module.id && !isModuleForm && toggleModuleForm(evt)"
            >
              <div class="builder-drawer-header-action builder-drawer-header-action__l">
                <fts-tippy content="Back to modules" append-trigger-class="builder-drawer-header-btn-text-ml" v-if="module.id">
                  <fts-btn
                    text
                    icon
                    class="stopPropagation builder-drawer-header-btn-rounded"
                    @click="evt => isModuleForm ? isModuleForm=false : selectDesign(evt, 'modules')"
                  >
                    <font-awesome-icon :icon="['fas', 'angle-left']" />
                  </fts-btn>
                </fts-tippy>
              </div>

              <template v-if="!isModuleForm">
                <div
                  class="builder-drawer-header-label"
                  :class="{'builder-drawer-header-label--unpublished': !module.published}"
                  v-if="module.id"
                >
                  <fts-label-group
                    :item="{
                      'name': module.name,
                      'emoji': module.emoji
                    }"
                    emoji
                    append-icon-class="builder-drawer-header-emoji"
                  />
                </div>
                <div class="builder-drawer-header-label" v-else>Modules</div>
              </template>

              <div class="builder-drawer-header-action builder-drawer-header-action__r">
                <template v-if="module.id">
                  <fts-event-toggle
                    event-type="notification"
                    :count="moduleNotificationCount"
                    class="builder-drawer-header-action-toggle stopPropagation"
                    @open="addAutomation({ fieldName: 'vector' }, 'notification')"
                  />
                  <fts-event-toggle
                    :count="moduleAutomationCount"
                    class="builder-drawer-header-action-toggle stopPropagation"
                    @open="addAutomation({ fieldName: 'vector' }, 'automation')"
                  />
                  <fts-auth type="vector" append-toggle-class="builder-drawer-header-action-toggle"  class="stopPropagation" />
                </template>

                <fts-tippy content="Create new Module" v-else>
                  <fts-btn
                    color="primary"
                    outline
                    text-transform-init
                    box-shadow-none
                    class="builder-drawer-header-btn"
                    @click="openModuleCreateModal"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" /> New
                  </fts-btn>
                </fts-tippy>
              </div>
            </div>

            <template v-if="selectedDesign === 'modules'">
              <div class="builder-drawer__content fts-scrollbar">
                <fts-module-designer
                  v-model="workspace.menus"
                  @module:select="selectModule"
                  @module:delete="deleteModuleConfirmation"
                  @change="save"
                />
              </div>
            </template>

            <template v-if="module.id">
              <fts-module-form
                :module="module"
                :modules="workspaceModules"
                :automation="moduleAutomation"
                @cancel="isModuleForm=false"
                @module:update="updateModule"
                @module:delete="deleteModule"
                @automation:open="openAutomationDesignModal"
                class="builder-drawer-module-form fts-scrollbar"
                v-if="isModuleForm"
              />
              <template v-else>
                <div style="display: flex;align-items: center;margin-left: 0.5rem;margin-right: 0.5rem;margin-bottom: 0.5rem;">
                  <fts-btn
                    sm
                    icon
                    text
                    color="var(--fts-ai)"
                    class="builder-ai-intention"
                    @click="openAIIntentionModal"
                    v-if="isInternal"
                  >
                    <i class="fts-icon-magic-lamp inistate-text-ai" />
                  </fts-btn>

                  <div class="fts-form-control-filter builder-filter">
                    <input
                      type="text"
                      class="fts-form-control builder-filter-input"
                      placeholder="Search"
                      v-model="filterKeyword"
                      @input="setFilterKeyword"
                    />
                    <div class="times-icon" @click="filterKeyword=null;setFilterKeyword();" v-if="filterKeyword">
                      <font-awesome-icon :icon="['fas', 'times']" fixed-width />
                    </div>
                    <div class="search-icon" v-else>
                      <font-awesome-icon :icon="['fas', 'search']" fixed-width />
                    </div>
                  </div>
                </div>

                <template v-for="card in cards">
                  <div
                    :key="`header-${card.id}`"
                    class="builder-drawer__header builder-drawer__header-design"
                    @click="selectDesign($event, card.activeKey[0])"
                  >
                    <div class="builder-drawer-header-label">
                      <div class="builder-drawer-header-label-name">{{card.name}}</div>
                      <fts-release-badge class="builder-drawer-header-label-badge" v-if="card.isBeta" />
                      <div class="builder-drawer-header-label-count">
                        ({{card.activeKey.reduce((accumulator, currentValue) => accumulator + cardElementsCount[currentValue], 0)}})
                      </div>
                    </div>
                    <fts-tippy :content="`Create new ${card.name}`">
                      <fts-dropdown-select
                        class="stopPropagation"
                        @selected="selectDesignActionCreate"
                        v-if="card.designElements.length > 1"
                      >
                        <fts-btn
                          slot="toggle"
                          color="primary"
                          outline
                          text-transform-init
                          box-shadow-none
                          class="builder-drawer-header-btn"
                        >
                          <font-awesome-icon :icon="['fas', 'plus']" /> New
                        </fts-btn>
                        <template #menu="{ select }">
                          <div
                            v-for="x in card.designElements.filter((y) => !y.disabled && y.creatable)"
                            :key="x.id"
                            class="dropdown-option"
                            @click="select(x)"
                          >
                            <div class="dropdown-option-icon dropdown-option-icon--primary">
                              <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
                            </div>
                            <div>{{x.displayName}}</div>
                          </div>
                        </template>
                      </fts-dropdown-select>

                      <fts-btn
                        color="primary"
                        outline
                        text-transform-init
                        box-shadow-none
                        class="builder-drawer-header-btn"
                        @click="openConsoleElement(card.designElements[0])"
                        v-else-if="!card.designElements[0].disabled && card.designElements[0].creatable"
                      >
                        <font-awesome-icon :icon="['fas', 'plus']" /> New
                      </fts-btn>
                    </fts-tippy>
                  </div>
                  
                  <template v-if="card.activeKey.indexOf(selectedDesign) > -1">
                    <div :key="`toggle-${card.id}`" class="builder-drawer__toggle" v-if="card.id === 'information'">
                      <div
                        class="builder-drawer-toggle"
                        :class="{'builder-drawer-toggle--active': selectedDesign === 'information'}"
                        @click="selectedDesign='information'"
                      >Design ({{cardElementsCount['information']}})</div>
                      <div
                        class="builder-drawer-toggle"
                        :class="{'builder-drawer-toggle--active': selectedDesign === 'informationDefault'}"
                        @click="selectedDesign='informationDefault'"
                      >Basic ({{cardElementsCount['informationDefault']}})</div>
                    </div>

                    <div :key="`toggle-${card.id}`" class="builder-drawer__toggle" v-else-if="card.id === 'activity'">
                      <div
                        class="builder-drawer-toggle"
                        :class="{'builder-drawer-toggle--active': selectedDesign === 'activity'}"
                        @click="selectedDesign='activity'"
                      >Design ({{cardElementsCount['activity']}})</div>
                      <div
                        class="builder-drawer-toggle"
                        :class="{'builder-drawer-toggle--active': selectedDesign === 'standardActivity'}"
                        @click="selectedDesign='standardActivity'"
                      >Basic ({{cardElementsCount['standardActivity']}})</div>
                    </div>

                    <div :key="`toggle-${card.id}`" class="builder-drawer__toggle" v-else-if="card.designElements.length > 1">
                      <div
                        v-for="x in card.designElements"
                        :key="x.id"
                        class="builder-drawer-toggle"
                        :class="{'builder-drawer-toggle--active': selectedDesign === x.name}"
                        @click="evt => selectDesign(evt, x.name)"
                      >{{x.displayName}} ({{cardElementsCount[x.name]}})</div>
                    </div>
                  </template>

                  <div
                    :id="`builder-drawer-content-${card.name}`"
                    :key="`content-${card.id}`"
                    class="builder-drawer__content fts-scrollbar"
                    :ref="`builder-drawer-content-${card.id}`"
                    v-if="card.activeKey.indexOf(selectedDesign) > -1"
                  >
                    <template v-for="x in card.designElements">
                      <fts-console-element
                        :key="x.id"
                        :parent-id="`builder-drawer-content-${card.name}`"
                        :selected-id="selectedDesignElementId"
                        :canvas.sync="mutableCanvas"
                        :item="x.fieldName"
                        :type="x.name"
                        :label="x.displayName"
                        :draggable="x.draggable"
                        :creatable="x.creatable"
                        :editable="x.editable"
                        :authorization="x.authorization"
                        :automation="x.automation"
                        @update="evt => openConsoleElement(x, evt)"
                        @openAutomation="openAutomationDesignModal"
                        @list:change="save({ 'type': x.name, 'action': 'change' });"
                        @scroll:to="evt => scrollToDesignElement(`builder-drawer-content-${card.id}`, evt)"
                        v-if="selectedDesign === x.name"
                      />
                    </template>

                    <fts-console-element
                      :parent-id="`builder-drawer-content-${card.name}`"
                      :selected-id="selectedDesignElementId"
                      :canvas="informationSystemDefault"
                      :item="DesignElement.Information.fieldName"
                      :type="DesignElement.Information.name"
                      :label="DesignElement.Information.displayName"
                      :draggable="false"
                      :creatable="false"
                      :editable="false"
                      :authorization="false"
                      :authorization-rule="x => x.id === 'state'"
                      :automation="false"
                      system
                      @update="evt => openConsoleElement(DesignElement.Information, evt)"
                      @openAutomation="openAutomationDesignModal"
                      @scroll:to="evt => scrollToDesignElement(`builder-drawer-content-${card.id}`, evt)"
                      v-if="selectedDesign === 'informationDefault'"
                    />
                    <fts-console-element
                      :parent-id="`builder-drawer-content-${card.name}`"
                      :selected-id="selectedDesignElementId"
                      :canvas.sync="mutableCanvas"
                      :item="Activity_System_Default.fieldName"
                      :type="Activity_System_Default.name"
                      :label="Activity_System_Default.displayName"
                      :page-size="Activity_System_Default.pageSize"
                      :draggable="Activity_System_Default.draggable"
                      :creatable="Activity_System_Default.creatable"
                      :editable="Activity_System_Default.editable"
                      :authorization="Activity_System_Default.authorization"
                      :automation="Activity_System_Default.automation"
                      @update="evt => openConsoleElement(Activity_System_Default, evt)"
                      @openAutomation="openAutomationDesignModal"
                      @scroll:to="evt => scrollToDesignElement(`builder-drawer-content-${card.id}`, evt)"
                      v-if="selectedDesign === 'standardActivity'"
                    />
                  </div>
                </template>
              </template>
            </template>
          </div>
        </div>
      </div>

      <v-dialog />
      <fts-confirmation global />
      <fts-console-validator />
      <fts-user-profile ref="ftsUserProfile" />
      <fts-invitation-modal ref="ftsInvitationModal" />
      <fts-workspace-modal ref="ftsWorkspaceModal" />
      <fts-module-create-modal :modules="workspaceModules" ref="ftsModuleCreateModal" />
      <fts-ai-intention-modal ref="ftsAIIntentionModal" />
      <fts-automation-design-modal ref="ftsAutomationDesignModal" />
      <fts-action-design-modal ref="ftsActionDesignModal" />
      <fts-activity-design-modal
        builder
        :is-capable-write="isCapableOf(SYSTEM_CAPABILITY.Write)"
        :is-capable-workflow="isCapableOf(SYSTEM_CAPABILITY.Workflow)"
        :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
        ref="ftsActivityDesignModal"
      />
      <fts-standard-activity-design-modal
        builder
        :is-capable-write="isCapableOf(SYSTEM_CAPABILITY.Write)"
        :is-capable-workflow="isCapableOf(SYSTEM_CAPABILITY.Workflow)"
        :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
        ref="ftsStandardActivityDesignModal"
      />
      <fts-information-design-modal
        :is-capable-add-information="isCapableOf(SYSTEM_CAPABILITY.AddInformation)"
        :is-capable-change-information="isCapableOf(SYSTEM_CAPABILITY.ChangeInformation)"
        ref="ftsInformationDesignModal"
      />
      <fts-state-design-modal builder ref="ftsStateDesignModal" />
      <fts-flow-design-modal ref="ftsFlowDesignModal" />
      <fts-listing-design-modal ref="listingDesign" />
      <fts-document-design-modal ref="ftsDocumentDesignModal" />
      <fts-logic-service-design-modal ref="ftsLogicServiceDesignModal" />
      <fts-import-design-modal ref="ftsImportDesignModal" />
      <fts-run-app-modal ref="ftsRunAppModal" />
      <!-- workflow: the Studio's editor, reused until the Builder itself is removed -->
      <fts-workflow ref="ftsWorkflow" @done="onWorkflowDone" />
      <!-- create workspace modal -->
      <fts-workspace-create-modal ref="workspaceCreateModal" @created="onWorkspaceCreated" />
    </template>
    <div class="builder-loading-overlay" v-if="isDeviceLoading">
      <fts-spinner />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import VueCookies from "vue-cookies";
Vue.use(VueCookies);
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import changesets from "diff-json";
import moment from 'moment';

import { authorizationStore } from './components/store/authorizationStore';
import { store } from './components/store/canvasDesignStore';
import { classificationStore } from './components/store/classificationStore';
import LocalStorageService from './components/store/LocalStorageService';
import webAppConfig from './components/commons/webApp.config';
import commons from './components/commons/commons';
import { billingReturnSection, clearBillingReturn, readBillingReturn } from './components/commons/billing';
import { isCapableOf, SYSTEM_CAPABILITY } from './components/commons/flags';
import canvasDesign from './components/commons/canvasDesign';
import formDesign from './components/commons/formDesign';
import designValidator from './components/commons/designValidator';

import NotificationControlPanel from './components/administration/NotificationControlPanel';
import FTSAIIntentionModal from './components/design/FTSAIIntentionModal';
import FTSBtn from './components/elements/FTSBtn';
import FTSConfirmation from './components/elements/confirmation/FTSConfirmation';
import FTSDropdownSelect from './components/elements/FTSDropdownSelect';
import FTSProfile from './components/elements/FTSProfile';
import FTSUserProfile from './components/elements/FTSUserProfile';
import FTSSpinner from './components/elements/FTSSpinner';
import FTSTippy from './components/elements/FTSTippy';
import FTSToggleSwitch from './components/elements/FTSToggleSwitch';
import FTSInvitationModal from './components/client/FTSInvitationModal';
import FTSLabelGroup from './components/elements/FTSLabelGroup';
import FTSDashboard from './components/design/dashboard/FTSDashboard';
import FTSAuth from "./components/design/FTSAuth";
import FTSConsoleElement from './components/design/FTSConsoleElement';
import FTSConsoleValidator from './components/design/FTSConsoleValidator';
import FTSWorkspaceModal from './components/design/FTSWorkspaceModal';
import FTSModuleDesigner from './components/design/FTSModuleDesigner';
import FTSAutomationDesignModal from './components/design/FTSAutomationDesignModal';
import FTSActionDesignModal from './components/design/FTSActionDesignModal';
import FTSActivityDesignModal from './components/design/FTSActivityDesignModal';
import FTSStandardActivityDesignModal from './components/design/FTSStandardActivityDesignModal';
import FTSInformationDesignModal from './components/design/FTSInformationDesignModal';
import FTSRunAppModal from './components/design/FTSRunAppModal';
import FTSStateDesignModal from './components/design/FTSStateDesignModal';
import FTSFlowDesignModal from './components/design/FTSFlowDesignModal';
import FTSListingDesignModal from './components/design/FTSListingDesignModal';
import FTSDocumentDesignModal from './components/design/FTSDocumentDesignModal';
import FTSLogicServiceDesignModal from './components/design/FTSLogicServiceDesignModal';
import FTSImportDesignModal from './components/design/FTSImportDesignModal';
import FTSWorkflow from './components/elements/FTSWorkflow';
import FTSEventToggle from './components/design/event/FTSEventToggle';
import FTSBuilderDevice from './components/builder/FTSBuilderDevice';
import FTSModuleCreateModal from './components/builder/FTSModuleCreateModal';
import FTSModuleForm from './components/builder/FTSModuleForm';
import FTSRunAs from './components/elements/FTSRunAs';
import FTSModal from './components/elements/FTSModal';
import FTSWorkspaceCreateModal from './components/client/FTSWorkspaceCreateModal';

let deviceResolve = null;

export default {
  metaInfo() {
    return { title: this.metaTitle };
  },
  components: {
    'notification-control-panel': NotificationControlPanel,
    'fts-ai-intention-modal': FTSAIIntentionModal,
    'fts-btn': FTSBtn,
    'fts-confirmation': FTSConfirmation,
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-profile': FTSProfile,
    'fts-user-profile': FTSUserProfile,
    'fts-spinner': FTSSpinner,
    'fts-tippy': FTSTippy,
    'fts-toggle-switch': FTSToggleSwitch,
    'fts-invitation-modal': FTSInvitationModal,
    'fts-label-group': FTSLabelGroup,
    'fts-dashboard': FTSDashboard,
    'fts-auth': FTSAuth,
    'fts-console-element': FTSConsoleElement,
    'fts-console-validator': FTSConsoleValidator,
    'fts-workspace-modal': FTSWorkspaceModal,
    'fts-module-designer': FTSModuleDesigner,
    'fts-automation-design-modal': FTSAutomationDesignModal,
    'fts-action-design-modal': FTSActionDesignModal,
    'fts-activity-design-modal': FTSActivityDesignModal,
    'fts-standard-activity-design-modal': FTSStandardActivityDesignModal,
    'fts-information-design-modal': FTSInformationDesignModal,
    'fts-run-app-modal': FTSRunAppModal,
    'fts-state-design-modal': FTSStateDesignModal,
    'fts-flow-design-modal': FTSFlowDesignModal,
    'fts-listing-design-modal': FTSListingDesignModal,
    'fts-document-design-modal': FTSDocumentDesignModal,
    'fts-logic-service-design-modal': FTSLogicServiceDesignModal,
    'fts-import-design-modal': FTSImportDesignModal,
    'fts-workflow': FTSWorkflow,
    'fts-event-toggle': FTSEventToggle,
    'fts-builder-device': FTSBuilderDevice,
    'fts-module-create-modal': FTSModuleCreateModal,
    'fts-module-form': FTSModuleForm,
    'fts-run-as': FTSRunAs,
    'fts-modal': FTSModal,
    'fts-workspace-create-modal': FTSWorkspaceCreateModal,
  },
  store,
  data: () => ({
    SYSTEM_CAPABILITY: Object.freeze(SYSTEM_CAPABILITY),
    Activity_System_Default: Object.freeze({
      name: 'standardActivity',
      fieldName: 'standardActivities',
      displayName: 'Basic',
      description: 'actions that app commonly do',
      creatable: false,
      editable: false,
      draggable: false,
      authorization: true,
      automation: true,
      disabled: false,
      learnMore: `${commons.communityURL}/t/fetias101-standard/88`
    }),
    baseLocationOrigin: null,
    builderSync: true,
    filterKeyword: null,
    page: 'app',
    deviceWebUrl: null,
    deviceAppUrl: null,
    deviceWebActionMessage: null,
    deviceAppActionMessage: null,
    deviceFullView: false,
    selectedDevice: 'app',
    selectedDesign: 'modules',
    selectedDesignElementId: null,
    workspaceLoading: false,
    workspaceSearch: '',
    workspaceOptions: [],
    isModuleForm: false,
    authorizationEntities: [],
    isReady: false,
    isDeviceLoading: true,
    submitting: false,
    submitResponse: null,
    isRunAs: false,
  }),
  methods: {
    moment,
    isCapableOf(type) {
      return isCapableOf(type, this.workspaceModules, this.module.id);
    },
    resetModuleState() {
      this.$store.dispatch("resetVector");
      this.mutableFilterKeyword = null;
      this.filterKeyword = null;
      this.isModuleForm = false;
      this.selectedDesign = "modules";
      this.selectedDesignElementId = null;
      this.deviceAppActionMessage = null;
      this.deviceWebActionMessage = null;
    },
    async getWorkspaceOptions() {
      this.workspaceSearch = "";
      this.workspaceOptions = await this.$store.dispatch("searchWorkspace", {
        search: this.workspaceSearch,
        workspaceId: this.workspace.id
      });
      this.$nextTick(() => {
        this.$refs.workspaceSearch.focus();
      })
    },
    async selectWorkspace(workspace) {
      if (workspace && workspace.id && (workspace.id != this.workspace.id)) {
        try {
          this.workspaceLoading = true;
          this.isRunAs = false;

          this.resetModuleState();
          await this.$store.dispatch("selectWorkspace", { id: workspace.id });

          this.setDeviceUrl('app');
          this.setDeviceUrl('web');

          if (!(this.workspaceModules && this.workspaceModules.length)) {
            this.openModuleCreateModal();
          }
        } catch(err) {
          this.$toasted.global.error({
            response: err.response,
            message: "Oops! Unable to load the workspace at the moment. Please try again."
          });
        } finally {
          this.workspaceLoading = false;
        }
      }
    },
    /**
     * Back from Stripe Checkout (`?billing=…&ws=…` on the server query, which the hash router
     * leaves alone): open that workspace's Plan & Credits tab so it can confirm the outcome.
     * Mirrors Client.openBillingReturn; a billing email's `open` lands on the same tab. AutoCount
     * has no tab in this dialog: its Checkout return and its emails go to Settings › AutoCount.
     */
    async openBillingReturn() {
      const ret = readBillingReturn(window.location.search);
      if (!ret) return;
      if (ret.kind === 'open') clearBillingReturn();
      try {
        if (ret.workspaceId && this.workspace && ret.workspaceId != this.workspace.id) {
          await this.selectWorkspace({ id: ret.workspaceId });
        }
        if (ret.kind === 'open' && ret.workspaceId && !(this.workspace && ret.workspaceId == this.workspace.id)) return;
        if (billingReturnSection(ret) === 'autocount') {
          this.$router.push({ name: 'settings', params: { section: 'autocount' } }).catch(() => {});
          return;
        }
        await this.selectWorkspaceOption('edit', { section: 'subscription' });
      } catch (err) {
        console.error('billing return', err);
      }
    },
    async selectWorkspaceOption(item, modalOptions) {
      if (item == 'edit') {
        const res = await this.$refs.ftsWorkspaceModal.show({ id: this.workspace.id, ...(modalOptions || {}) });
        if (res && res.name) {
          this.$store.dispatch("setWorkspaceName", res.name);
        } else if (res && res.type == "delete") {
          this.workspaceLoading = true;
          let selectedWorkspaceId = null;

          try {
            selectedWorkspaceId = await this.$store.dispatch("getFirstOrDefaultWorkspaceId");
          } catch(err) {
            console.error(err);
          }

          if (selectedWorkspaceId) {
            this.selectWorkspace({ id: selectedWorkspaceId });
          } else {
            location.reload(true);
          }
        } else if (res && res.id) {
          this.selectWorkspace({ id: res.id });
        }
      } else if (item == "create") {
        this.$refs.workspaceCreateModal.open();
      } else {
        this.selectWorkspace(item);
      }
    },
    async onWorkspaceCreated(workspaceId) {
      await this.$store.dispatch("selectWorkspace", { id: workspaceId });

      if (this.workspace.id != workspaceId) {
        this.$toasted.global.error(new Error("Workspace was created, but we couldn't switch to it. Please refresh and try again."));
        return;
      }

      this.resetModuleState();
      this.setDeviceUrl('app');
      this.setDeviceUrl('web');

      if (!this.workspaceModules.length) {
        this.$nextTick(() => {
          this.openModuleCreateModal();
        });
      }
    },
    onSearchWorkspace: _.debounce(async function() {
      this.workspaceOptions = await this.$store.dispatch("searchWorkspace", { search: this.workspaceSearch });
    }, 800),
    async openModuleCreateModal() {
      const res = await this.$refs.ftsModuleCreateModal.show();
      if (res) {
        try {
          await this.$store.dispatch("addVector", { ...res });
          this.initModuleCanvas();
          /* workaround to get lastest workspace lastUpdatedOn */
          await this.$store.dispatch("loadWorkspace", { id: this.workspace.id });
          /* end */
          this.$toasted.success(`${this.module.name} has been created.`);
          this.isModuleForm = false;
          this.selectedDesign = 'menu';
          this.selectedDesignElementId = null;
          const url = `${this.baseLocationOrigin}/#/list/${commons.encodeids(this.module.id)}`;
          this.postActionToApp({'url': url});
          this.postActionToWeb({'url': url});
        } catch(err) {
          this.$toasted.global.error({
            response: err.response,
            message: "Failed to create module"
          });
        }
      }
    },
    toggleModuleForm(evt) {
      if (evt && commons.eventContainClassName(evt, 'stopPropagation')) {
        return;
      }
      this.isModuleForm = true;
    },
    async deleteModule(module) {
      try {
        this.resetModuleState();
        await this.$store.dispatch("deleteVector", module.id);
        this.$toasted.success(`${module.name} has been deleted.`);
        this.postActionToApp({'url': `${this.baseLocationOrigin}?refresh=true`});
        this.postActionToWeb({'url': `${this.baseLocationOrigin}/#/dashboard?refresh=true`});
      } catch(err) {
        this.$toasted.global.error({
          response: err.response,
          message: 'Failed to delete module'
        });
      }
    },
    async deleteModuleConfirmation(module) {
      const res = await this.$confirmation.show({
        type: "delete",
        title: "Module",
        text: module.name,
        textEmoji: module.emoji,
        enableConfirmationKey: true
      });
      if (res === true) {
        this.deleteModule(module);
      }
    },
    updateModule(module) {
      this.isModuleForm = false;
      this.$store.dispatch("updateModule", { ...this.module, ...module });
      this.save({'type': 'module', 'action': 'update'});
    },
    initModuleCanvas() {
      let canvas = {};
      [
        this.Activity_System_Default.fieldName, 
        this.DesignElement.Activity.fieldName, 
        this.DesignElement.Information.fieldName, 
        this.DesignElement.State.fieldName, 
        this.DesignElement.Flow.fieldName,
        this.DesignElement.Menu.fieldName,
        this.DesignElement.Document.fieldName,
        this.DesignElement.Action.fieldName,
        this.DesignElement.LogicService.fieldName
      ].forEach((x) => {
        if (!this.mutableCanvas[x]) {
          Object.assign(canvas, { [x]: [] });
        }
      });
      this.mutableCanvas = Object.assign({}, this.mutableCanvas, canvas);
    },
    async selectModule(module) {
      try {
        await this.$store.dispatch("loadVector", module.id);
        this.initModuleCanvas();
        const url = `${this.baseLocationOrigin}/#/list/${commons.encodeids(this.module.id)}`;
        this.postActionToApp({'url': url});
        this.isModuleContainsMenu ?
          this.postActionToWeb({'url': url, 'redirect': false, 'action': 'expandListing', 'moduleId': this.module.id}) :
          this.postActionToWeb({'url': url});
        this.selectedDesign = 'menu';
      } catch(err) {
        console.error(err);
        this.$toasted.global.error({
          response: err.response,
          message: `${module.name} unable to select.`
        });
      }
    },
    setFilterKeyword: _.debounce(async function() {
      this.mutableFilterKeyword = this.filterKeyword;
    }, 800),
    selectDesign(evt, type) {
      if (evt && commons.eventContainClassName(evt, 'stopPropagation')) {
        return;
      }
      this.selectedDesign = type;
      if (
        (type !== 'state' && type !== 'flow') &&
        (this.page !== 'app' && this.page !== 'web')
      ) {
        this.page = this.selectedDevice || 'app';
      }
      if (type === 'modules') {
        this.resetModuleState();
        this.postActionToApp({'url': `${this.baseLocationOrigin}`})
        this.postActionToWeb({'url': `${this.baseLocationOrigin}/#/dashboard`});
      } else if (type === 'menu') {
        const url = `${this.baseLocationOrigin}/#/list/${commons.encodeids(this.module.id)}`;
        this.postActionToApp({'url': url});
        this.isModuleContainsMenu ?
          this.postActionToWeb({'url': url, 'redirect': false, 'action': 'expandListing', 'moduleId': this.module.id}) :
          this.postActionToWeb({'url': url});
      }
    },
    selectDesignActionCreate(designElement) {
      this.selectDesign(null, designElement.name);
      this.openConsoleElement(designElement);
    },
    scrollToDesignElement(parentRef, el) {
      try {
        const targetEl = this.$refs[parentRef][0];
        const targetElClientRects = targetEl.getClientRects()[0];
        const elClientRects = el.getClientRects()[0];
        const getElClientRectsTopAndHeight = () => {
          try {
            return elClientRects.top + (elClientRects.height > targetElClientRects.height ? targetElClientRects.height : elClientRects.height);
          } catch(err) {
            console.error(err);
            return elClientRects.top + 45;
          }
        }
        if (targetElClientRects.top > elClientRects.top) 
          targetEl.scrollTop = targetEl.scrollTop - (targetElClientRects.top - elClientRects.top);
        else if (getElClientRectsTopAndHeight() > targetElClientRects.bottom)
          targetEl.scrollTop = targetEl.scrollTop + (getElClientRectsTopAndHeight() - targetElClientRects.bottom);
      } catch(err) {
        console.error(err);
      }
    },
    selectPage(page) {
      this.page = page;
      this.$nextTick(() => {
        if (page === 'app' && this.deviceAppActionMessage) {
          this.selectedDevice = 'app';
          if (
            this.selectedDesignElementId === 'quickView' ||
            this.selectedDesignElementId === 'createEditView' && this.deviceAppActionMessage.url.indexOf('a=') < 0
          ) {
            this.verifyStandardFormDesign('view');
          }
          this.builderSyncEnabled && this.$refs.ftsBuilderDevice.postActionToApp(this.deviceAppActionMessage);
        } else if (page === 'web' && this.deviceWebActionMessage) {
          this.selectedDevice = 'web';
          if (
            this.selectedDesignElementId === 'view' ||
            this.selectedDesignElementId === 'quickView' ||
            this.selectedDesignElementId === 'createEditView' && this.deviceAppActionMessage.url.indexOf('a=') < 0
          ) {
            if (this.deviceAppActionMessage.url.indexOf('m=f') > -1) {
              this.verifyStandardFormDesign('view');
            } else {
              this.verifyStandardFormDesign('quickView');
            }
          }
          this.builderSyncEnabled && this.$refs.ftsBuilderDevice.postActionToWeb(this.deviceWebActionMessage);
        } else if (page === 'dashboard' || page === 'notificationPanel') {
          this.resetModuleState();
        }
      });
    },
    async openConsoleElement(type, selected, params) {
      let vm = this;
      let item = { ...selected };

      if (!item.id && type.name !== "information") {
        item = {
          id: type.name === "menu" ? commons.nanoID() : commons.newUUID(),
          ...selected
        };

        if (type.name == "document") {
          item.version = 1.0;
        }
      }
      /* update */
      function set(result) {
        let items = vm.mutableCanvas[type.fieldName];

        if (!items) {
          items = [];
        }
        const findIndex = items.findIndex(x => x.id == result.id);
        if (findIndex > -1) {
          vm.$set(items, findIndex, result);
        } else if (type.name === "information") {
          let findDeep = _.findDeep(items, x => x.id == result.id, {
            childrenPath: "fields"
          });
          if (findDeep && findDeep.parent && findDeep.key) {
            vm.$set(findDeep.parent.fields, findDeep.key, result);
          } else {
            items.push(result);
          }
        } else {
          items.push(result);
        }
        /* add information fields to forms */
        if (
          (type.name === 'standardActivity' && result.design) ||
          (type.name === 'information' && (result.type == 14 || result.type == 15))
        ) {
          formDesign.addInformationFieldsToForms(vm.mutableCanvas);
        }
        /* update canvas */
        vm.$set(vm.mutableCanvas, type.fieldName, items);
        /* compact classification */
        commons.compactClassification(vm.mutableCanvas);
        /* update vector canvas */
        vm.$store.dispatch("updateVectorCanvas", vm.mutableCanvas);
        /* save */
        vm.save({'type': type.name, 'action': 'update', 'data': result});
      }
      /* remove */
      function remove() {
        let items = vm.mutableCanvas[type.fieldName];
        const findIndex = items.findIndex(x => x.id == item.id);
        if (findIndex > -1) {
          items.splice(findIndex, 1);
        } else if (type.name === "information") {
          let findDeep = _.findDeep(items, x => x.id == item.id, {
            childrenPath: "fields"
          });
          if (findDeep && findDeep.parent && findDeep.key) {
            vm.$delete(findDeep.parent.fields, findDeep.key);
          }
        }
        /* save */
        vm.save({'type': type.name, 'action': 'delete', 'data': item});
      }
      /* set alternative design */
      function setAlternativeDesign(canvas, items) {
        items.forEach(x => {
          if (x && x.type && x.extension) {
            if (x.type === "flow") {
              canvasDesign.setFlow(canvas, x.extension);
            }
          }
        });
      }
      if (type.name === "information") {
        if (item.system) {
          if (item.id === "title") {
            item.title = this.mutableCanvas.title;
          }
        } else if (!item.id) {
          item = {
            id: commons.generateFieldName(),
            fieldName: commons.generateFieldName(),
            ...selected
          };
        }
        const result = await this.$refs.ftsInformationDesignModal.show(
          item,
          null,
          continueFormData => {
            set(continueFormData);
            this.openConsoleElement(type);
          }
        );
        if (result != null && result != true) {
          if (result.system) {
            if (result.id === "title") {
              this.$set(this.mutableCanvas, "title", result.title);
            } else if (result.id == "documentId") {
              if (result.documentIdFormat) {
                this.$set(this.module, "documentIdFormat", result.documentIdFormat);
              } else {
                this.$delete(this.module, "documentIdFormat");
              }
            }
            this.save();
          } else {
            set(result);
          }
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "menu") {
        const result = await this.$refs.listingDesign.show(item);
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name == "document") {
        const result = await this.$refs.ftsDocumentDesignModal.show(item);
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "activity") {
        const setActivity = (formData, alternativeDesign) => {
          let canvas = _.cloneDeep(this.$store.state.canvas);
          /* set activity */
          canvasDesign.setActivity(canvas, formData);
          /* alternative design */
          if (Boolean(alternativeDesign)) {
            setAlternativeDesign(canvas, alternativeDesign);
          }
          /* add information fields to forms */
          formData.design && formDesign.addInformationFieldsToForms(canvas);
          /* compact classification */
          commons.compactClassification(canvas);
          /* dispatch state */
          this.$store.dispatch("updateVectorCanvas", canvas);
          /* save */
          this.save({'type': type.name, 'action': 'update', 'data': formData});
        }
        const result = await this.$refs.ftsActivityDesignModal.show(
          item,
          null,
          /* continue callback */
          (continueFormData, alternativeDesign) => {
            setActivity(continueFormData, alternativeDesign);
            /* open design */
            this.openConsoleElement(type);
          },
          /* navigate callback */
          (
            navigateFormData,
            navigateType,
            navigateData,
            navigateParams,
            alternativeDesign
          ) => {
            setActivity(navigateFormData, alternativeDesign);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          },
          /* params */
          params
        );
        if (result != null && result != true) {
          if (result.formData) {
            setActivity(result.formData, result.alternativeDesign);
          } else {
            setActivity(result);
          }
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "standardActivity" && item.id === "import") {
        const result = await this.$refs.ftsImportDesignModal.show(
          this.mutableCanvas.import || {}
        );
        if (result != null && result != true) {
          this.$set(this.mutableCanvas, "import", result);
          this.save();
        } else if (result == true) {
          this.$delete(this.mutableCanvas, "import");
        }
      } else if (type.name === "standardActivity" && item.id) {
        const result = await this.$refs.ftsStandardActivityDesignModal.show(
          item,
          null,
          (navigateFormData, navigateType, navigateData, navigateParams) => {
            set(navigateFormData);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          }
        );
        if (result != null && result != true) {
          set(result);
        }
      } else if (type.name === "state") {
        const result = await this.$refs.ftsStateDesignModal.show(
          item,
          null,
          continueFormData => {
            set(continueFormData);
            this.openConsoleElement(type);
          },
          (navigateFormData, navigateType, navigateData, navigateParams) => {
            set(navigateFormData);
            /* open design */
            setTimeout(() => {
              this.openConsoleElement(
                navigateType,
                navigateData,
                navigateParams
              );
            }, 500);
          }
        );
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "flow") {
        const result = await this.$refs.ftsFlowDesignModal.show(item);
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "action") {
        const result = await this.$refs.ftsActionDesignModal.show(item);
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      } else if (type.name === "logicService") {
        const result = await this.$refs.ftsLogicServiceDesignModal.show(item);
        if (result != null && result != true) {
          set(result);
        } else if (result == true) {
          remove();
        }
      }
    },
    async openAutomationDesignModal(item) {
      const res = await this.$refs.ftsAutomationDesignModal.show(item);
      if (item.type === 'vector' && !this.isModuleForm) {
        this.isModuleForm = true;
      }
      if (res !== null && res !== true) {
        this.save();
      } else if (res === true) {
        this.save();
      }
    },
    addAutomation(payload, actionType) {
      this.openAutomationDesignModal({
        item: null,
        type: payload.fieldName,
        trigger: "execute",
        actionType: actionType
      });
    },
    openUserProfileModal() {
      this.$refs.ftsUserProfile.show();
    },
    openInvitation() {
      this.$refs.ftsInvitationModal.open({ workspaceName: this.workspace.name });
    },
    async openAIIntentionModal() {
      const getOptionMapped = (options, type) => {
        if (Array.isArray(options)) {
          return options.map(option => ({ 
            id:option.id, 
            value: option, 
            type: type
          }));
        }

        return [];
      }

      const res = await this.$refs.ftsAIIntentionModal.show({
        targetOptions: [
          ...getOptionMapped(this.$store.getters.canvasInformation, "information"),
          ...getOptionMapped(this.$store.getters.canvasMenus, "menu"),
          ...getOptionMapped(this.$store.getters.canvasActivities, "activity")
        ]
      });
    },
    setDeviceUrl(device, url) {
      const origin = webAppConfig.baseURL;
      const query = `accessToken=${LocalStorageService.getAccessToken()}&refreshToken=${LocalStorageService.getRefreshToken()}&wsId=${this.workspace.id}&origin=${origin}&builder=true`;
      this.baseLocationOrigin = webAppConfig.baseLocationOrigin;
      if (device === 'web') {
        this.deviceWebUrl = `${webAppConfig.previewWebURL}/#/dashboard?${query}`;
      } else if (device === 'app') {
        this.deviceAppUrl = url ?
          `${webAppConfig.previewAppURL}/?${query}&url=${url}` :
          `${webAppConfig.previewAppURL}/?${query}`;
      }
    },
    sanitizeDeviceUrl(url) {
      const paramString = url.split('?')[1];
      const searchParams = new URLSearchParams(paramString);
      searchParams.delete("wsId");
      searchParams.delete("accessToken");
      searchParams.delete("refreshToken");
      searchParams.delete("builder");
      searchParams.delete("origin");
      searchParams.delete("refresh");
      searchParams.delete("runas");
      return `${url.split('?')[0]}${Boolean(searchParams.toString()) ? `?${searchParams.toString()}` : ''}`;
    },
    getDeviceWebUrl() {
      let url = (this.deviceWebActionMessage && this.deviceWebActionMessage.url) || `${this.baseLocationOrigin}/#/dashboard`;
      return this.sanitizeDeviceUrl(url);
    },
    getDeviceAppUrl() {
      let url = (this.deviceAppActionMessage && this.deviceAppActionMessage.url) || `${this.baseLocationOrigin}`;
      return this.sanitizeDeviceUrl(url);
    },
    openWorkflow() {
      this.$refs.ftsWorkflow.show();
    },
    // the editor has put the layout in the store; save() takes no payload for it, as before
    onWorkflowDone() {
      this.save();
    },
    runApp() {
      this.$refs.ftsRunAppModal.show({
        workspaceId: this.workspace.id,
        workspaceName: this.workspace.name
      });
    },
    /**
     * @param {Object} payload
     * @param {String} payload.type
     * @param {String} payload.action
     * @param {Object} payload.data
     */
    save: _.debounce(async function(payload) {
      await this.executeSave(payload);
    }, 800),
    async executeSave(payload, { forceSubmit: _forceSubmit } = {}) {
      let forceSubmit = _forceSubmit;
      try {
        /* consistency check */
        if (this.module && this.module.id && !forceSubmit) {
          try {
            const consistency = await this.$store.dispatch("checkConsistency");
            if (!consistency.consistent) {
              console.error('[Consistency Check] Raw response:', JSON.parse(JSON.stringify(consistency)));
              const errorMessages = consistency.errors
                .map(g => g.items.map(i => `[${g.section}] ${i.message}`))
                .flat();
              const res = await this.$confirmation.show({
                title: "Inconsistency Detected",
                text: errorMessages.join('\n'),
                iconPack: "material-icons-outlined",
                icon: "warning",
                iconColor: "var(--inistate-icon-warning)",
                buttons: [
                  {
                    title: "Cancel",
                    bind: { text: true }
                  },
                  {
                    title: "Force Submit",
                    bind: { color: "warning" },
                    default: true,
                    handler: ({ resolve }) => resolve(true)
                  }
                ]
              });
              if (res !== true) return;
              forceSubmit = true;
            }
          } catch (err) {
            console.warn('Consistency check failed, proceeding with save', err);
          }
        }

        this.submitting = true;
        this.submitResponse = null;
        let workspaceResult = null;
        let moduleResult = null;

        if (this.workspace.id) {
          workspaceResult = await new Promise(async(resolve, reject) => {
            const toSaveMenus = commons.trimMenu(this.workspace.menus);
            const workspaceMenusDiff = changesets.diff(this.$store.getters.originalWorkspaceMenus, toSaveMenus);
            if (workspaceMenusDiff && workspaceMenusDiff.length) {
              try {
                const res = await this.$store.dispatch("saveWorkspace");
                this.$store.dispatch("selectWorkspace", {id: this.workspace.id});
                resolve(res);
              } catch(err) {
                reject(err);
              }
            } else {
              resolve(null);
            }
          });
        }

        if (this.module.id) {
          let canvas = _.cloneDeep(this.mutableCanvas);
          if (
            canvas.connectorText != '' &&
            canvas.connectorText != null
          ) {
            canvas.connector = JSON.parse(canvas.connectorText);
            delete canvas.connectorText;
          }
          if (
            canvas.mappingText != '' &&
            canvas.mappingText != null
          ) {
            canvas.mapping = JSON.parse(canvas.mappingText);
            delete canvas.mappingText;
          }

          try {
            canvasDesign.expandDocumentsFormDesign(canvas);
          } catch(err) { console.error(err); }

          // Views plus the page design (canvas.listDesign, R13): both carry column designs.
          const columnDesigns = (canvas.menus || []).concat(canvas.listDesign ? [canvas.listDesign] : []);
          canvasDesign.expandMenusDesign(columnDesigns, canvas.information);
          canvasDesign.compactMenusDesign(columnDesigns, canvas.information);

          this.mutableCanvas = canvas;
          moduleResult = await this.$store.dispatch("saveVector", {
            forceSubmit: forceSubmit
          });
        }

        const getCurrentUrl = (page) => {
          let url = null;
          if (page === 'app') {
            url = (this.deviceAppActionMessage && this.deviceAppActionMessage.url) || `${this.baseLocationOrigin}`;
          } else if (page === 'web') {
            url = (this.deviceWebActionMessage && this.deviceWebActionMessage.url) || `${this.baseLocationOrigin}/#/dashboard`;
          }
          if (url.includes('?')) {
            let params = new URLSearchParams(url.split('?')[1]);
            params.set('refresh', true);
            return `${url.split('?')[0]}?${params.toString()}`;
          } else {
            return `${url}?refresh=true`;
          }
        }

        if (Boolean(workspaceResult)) {
          this.submitResponse = {
            status: 200,
            updatedOn: moment()
          }
          this.postActionToApp({'url': getCurrentUrl('app')});
          this.postActionToWeb({'url': getCurrentUrl('web')});
        }
        if (Boolean(moduleResult)) {
          this.submitResponse = {
            status: 200,
            updatedOn: moduleResult.data.updatedDate
          }
          this.$store.dispatch("setWorkspaceLastUpdatedOn", moduleResult.data.updatedDate);
          if (payload) {
            const targetId = payload.data && payload.data.id;
            /* post action */
            if (payload.type === 'module') {
              if (this.module.published === false) {
                this.postActionToApp({'url': `${this.baseLocationOrigin}?refresh=true`}, true);
                this.postActionToWeb({'url': `${this.baseLocationOrigin}/#/dashboard?refresh=true`}, true);
                this.selectedDesignElementId = null;
              } else {
                if (this.selectedDesignElementId) {
                  this.postActionToApp({'url': getCurrentUrl('app')});
                  this.postActionToWeb({'url': getCurrentUrl('web')});
                } else {
                  const url = `${this.baseLocationOrigin}/#/list/${commons.encodeids(this.module.id)}?refresh=true`;
                  this.postActionToApp({'url': url});
                  this.postActionToWeb({'url': url});
                }
              }
            } else if (payload.type == 'information' || payload.type == 'action' || payload.type === 'document') {
              this.postActionToApp({'url': getCurrentUrl('app')});
              this.postActionToWeb({'url': getCurrentUrl('web')});
            } else if (payload.type === 'menu') {
              if (payload.action === 'change' || (payload.action === 'update' && targetId === this.selectedDesignElementId)) {
                this.postActionToApp({'url': getCurrentUrl('app')});
                this.postActionToWeb({'url': getCurrentUrl('web')});
              } else if (payload.action === 'create' || payload.action === 'update') {
                const url = `${this.baseLocationOrigin}/#/list/${commons.encodeids(this.module.id)}?l=${payload.data.id}&refresh=true`;
                this.postActionToApp({'url': url});
                this.postActionToWeb({'url': url});
                this.selectedDesignElementId = targetId;
              } else if (payload.action === 'delete') {
                if (!this.isModuleContainsMenu) {
                  const url = `${this.baseLocationOrigin}/#/list/${commons.encodeids(this.module.id)}?refresh=true`;
                  this.postActionToApp({'url': url});
                  this.postActionToWeb({'url': url});
                  this.selectedDesignElementId = null;
                } else if (targetId === this.selectedDesignElementId) {
                  this.postActionToApp({'url': `${this.baseLocationOrigin}?refresh=true`});
                  this.postActionToWeb({'url': `${this.baseLocationOrigin}/#/dashboard?refresh=true`});
                  this.selectedDesignElementId = null;
                } else {
                  this.postActionToApp({'url': getCurrentUrl('app')});
                  this.postActionToWeb({'url': getCurrentUrl('web')});
                }
              }
            } else if (payload.type === 'activity' || payload.type === 'standardActivity') {
              if (payload.type === 'standardActivity' && this.isStandardFormDesign(this.selectedDesignElementId)) {
                const deviceCurrentUrl = this.page === 'app' && this.deviceAppActionMessage && this.deviceAppActionMessage.url ?
                  this.deviceAppActionMessage.url :
                  this.page === 'web' && this.deviceWebActionMessage && this.deviceWebActionMessage.url ?
                    this.deviceWebActionMessage.url : null;

                if (deviceCurrentUrl) {
                  if (deviceCurrentUrl.indexOf('a=create') > -1) {
                    this.verifyStandardFormDesign('create');
                  } else if (deviceCurrentUrl.indexOf('a=edit') > -1) {
                    this.verifyStandardFormDesign('edit');
                  } else if (deviceCurrentUrl.indexOf('m=f') > -1 || this.page === 'app') {
                    this.verifyStandardFormDesign('view');
                  } else {
                    this.verifyStandardFormDesign('quickView');
                  }
                }
              }

              if (payload.action === 'delete') {
                if (targetId === this.selectedDesignElementId) {
                  this.postActionToApp({'url': `${this.baseLocationOrigin}?refresh=true`});
                  this.postActionToWeb({'url': `${this.baseLocationOrigin}/#/dashboard?refresh=true`});
                } else {
                  this.postActionToApp({'url': getCurrentUrl('app')});
                  this.postActionToWeb({'url': getCurrentUrl('web')});
                }
                this.selectedDesignElementId = null;
              } else {
                this.postActionToApp({'url': getCurrentUrl('app')});
                this.postActionToWeb({'url': getCurrentUrl('web')});
              }
            }
          }
        }
      } catch(err) {
        console.error(err);
        this.submitResponse = {
          status: err.response.status,
          isError: true
        }
        this.$toasted.global.error({
          response: err.response,
          message: 'Studio changes failed to save'
        });
      } finally {
        this.submitting = false;
      }
    },
    refreshPage() {
      if (this.isRunAs)  {
        this.isRunAs = false;
      }

      if (this.page === 'dashboard') {
        this.$refs.ftsDashboard.initDashboard();
      } else if (this.page === 'app' || this.page == 'web') {
        this.$refs.ftsBuilderDevice.reload();
        this.resetModuleState();
      }
    },
    async getEventMessage(evt) {
      if (
        evt.data &&
        (typeof evt.data === 'string' || evt.data instanceof String) &&
        evt.data.indexOf('inistate') > -1
      ) {
        try {
          const res = JSON.parse(evt.data);
          console.info(`From Event ${res.device || '(undefined)'} to Builder`, res);
          this.selectedDesignElementId = null;
          if (res.onload) {
            deviceResolve && deviceResolve();
          } else if (res.url) {
            const routerResolved = this.$router.resolve(res.url.substring(res.url.indexOf('#') + 1));
            const route = routerResolved && routerResolved.route;
            let moduleData = {};
            if (route) {
              if (route.params && route.params.moduleId) {
                moduleData.id = commons.decodeids(route.params.moduleId);
              }
              if (route.query) {
                if (route.query.l) {
                  moduleData.listingId = route.query.l;
                }
                if (route.query.a) {
                  moduleData.activityId = route.query.a;
                }
                if (route.query.id) {
                  const queryDecodeids = commons.queryDecodeids(route.query.id);
                  if (queryDecodeids && queryDecodeids.entryId) {
                    moduleData.entryId = route.query.id;
                  }
                  if (queryDecodeids && queryDecodeids.moduleId) {
                    moduleData.id = queryDecodeids.moduleId;
                  }
                }
                if (!moduleData.activityId && moduleData.entryId) {
                  if (this.page === 'app') {
                    moduleData.activityId = 'view';
                  } else {
                    moduleData.activityId = route.query.m && route.query.m === 'f' ? 'view' : 'quickView';
                  }
                }
              }

              if (moduleData.id && (!this.module.id || (this.module.id != moduleData.id))) {
                try {
                  await this.$store.dispatch("loadVector", moduleData.id);
                  this.initModuleCanvas();
                } catch(err) { 
                  this.$toasted.global.error({
                    response: err.response,
                    message: 'Unauthorized Access'
                  });
                  this.isDeviceLoading = true;
                  this.resetModuleState();
                  this.postActionToApp({'url': `${this.baseLocationOrigin}`});
                  this.postActionToWeb({'url': `${this.baseLocationOrigin}/#/dashboard`});
                  const wsId = await this.$store.dispatch("getFirstOrDefaultWorkspaceId");
                  await this.$store.dispatch("loadWorkspace", { id: wsId });
                  this.setDeviceUrl("app");
                  this.setDeviceUrl("web");
                  this.isDeviceLoading = false;
                  return;
                }
              }
              /* sync with Builder */
              if (this.builderSync) {
                if (moduleData.activityId) {
                  this.selectedDesign = this.mutableCanvas.standardActivities.some(x => x.id == moduleData.activityId) ?
                    'standardActivity' : 'activity';
                  this.selectedDesignElementId = moduleData.activityId;
                  this.selectedDesign === 'standardActivity' && this.verifyStandardFormDesign(this.selectedDesignElementId);
                } else if (route.name === 'list-view') {
                  this.selectedDesign = 'menu';
                  if (moduleData.listingId) {
                    this.selectedDesignElementId = moduleData.listingId;
                  }
                } else if (['dashboard', 'calendar', 'users'].indexOf(route.name) > -1) {
                  this.resetModuleState();
                }
              }
              /* sync App with Web */
              this.deviceAppActionMessage = {'url': res.url};
              if (route.name === 'list-view' && !route.query.l && this.isModuleContainsMenu) {
                this.deviceWebActionMessage = {'url': `${this.baseLocationOrigin}/#${route.fullPath}`, 'redirect': false, 'action': 'expandListing', 'moduleId': this.module.id};
              } else {
                this.deviceWebActionMessage = {'url': `${this.baseLocationOrigin}/#${route.fullPath}`};
              }
              /* temp, due to app dashboard link to error page */
              if (this.deviceAppActionMessage.url && this.deviceAppActionMessage.url.includes('/dashboard')) {
                this.deviceAppActionMessage = {'url': this.baseLocationOrigin}
              }
            }
          }
        } catch(err) { console.error(err); }
      }
    },
    isStandardFormDesign(activityId) {
      return ['createEditView', 'create', 'edit', 'view', 'quickView'].indexOf(activityId) > -1;
    },
    verifyStandardFormDesign(activityId) {
      try {
        if (activityId === 'quickView') {
          const res = this.mutableCanvas.standardActivities.find(x => x.id === 'quickView');
          if (res.design) {
            this.selectedDesignElementId = 'quickView';
          } else {
            const findView = this.mutableCanvas.standardActivities.find(x => x.id === 'view');
            this.selectedDesignElementId = findView.design ? 'view' : 'createEditView';
          }
        } else if (['create', 'edit', 'view'].indexOf(activityId) > -1) {
          const res = this.mutableCanvas.standardActivities.find(x => x.id === activityId);
          this.selectedDesignElementId = res.design ? activityId : 'createEditView';
        }
      } catch(err) {
        console.error(err);
      }
    },
    setDeviceRunAs(payload) {
      const runAsTarget = `runas=${payload && payload.target ? payload.target : ""}`;
      let webUrl = this.getDeviceWebUrl();
      webUrl += webUrl.includes("?") ? `&${runAsTarget}` : `?${runAsTarget}`;
      let appUrl = this.getDeviceAppUrl();
      appUrl += appUrl.includes("?") ? `&${runAsTarget}` : `?${runAsTarget}`;
      this.$refs.ftsBuilderDevice.postActionToWeb({"url": webUrl});
      this.$refs.ftsBuilderDevice.postActionToApp({"url": appUrl});
    },
    postActionToWeb(payload, forcePostAction) {
      if (this.page === 'web' && (this.builderSyncEnabled || forcePostAction)) {
        this.$refs.ftsBuilderDevice.postActionToWeb(payload);
      }
      this.deviceWebActionMessage = payload;
    },
    postActionToApp(payload, forcePostAction) {
      if (this.page === 'app' && (this.builderSyncEnabled || forcePostAction)) {
        this.$refs.ftsBuilderDevice.postActionToApp(payload);
      }
      this.deviceAppActionMessage = payload;
    },
    async deviceOnload() {
      return await new Promise(resolve => deviceResolve = resolve);
    }
  },
  computed: {
    DesignElement() {
      return {
        Activity: {
          name: "activity",
          fieldName: "activities",
          displayName: "Activity",
          description: "additional actions for your team",
          creatable: true,
          editable: true,
          authorization: true,
          automation: true,
          disabled: false,
          relyOn: [],
          learnMore: `${commons.communityURL}/t/fetias101-activity/82`
        },
        Information: {
          name: "information",
          fieldName: "information",
          displayName: "Information",
          description: "data or fields",
          creatable: this.isCapableOf(SYSTEM_CAPABILITY.AddInformation),
          editable: true,
          authorization: false,
          automation: true,
          disabled: false,
          relyOn: [],
          learnMore: `${commons.communityURL}/t/fetias101-information/81`
        },
        State: {
          name: "state",
          fieldName: "states",
          displayName: "State",
          relyOn: [],
          description: "status showing the progress",
          creatable: true,
          editable: true,
          authorization: false,
          automation: true,
          disabled: !this.isCapableOf(SYSTEM_CAPABILITY.Workflow),
          learnMore: `${commons.communityURL}/t/fetias101-state/83`
        },
        Flow: {
          name: "flow",
          fieldName: "flows",
          displayName: "Flow",
          description: "how a state changed to another by activity",
          creatable: true,
          editable: true,
          authorization: false,
          automation: false,
          disabled: !this.isCapableOf(SYSTEM_CAPABILITY.Workflow),
          relyOn: ["state", "activity"],
          learnMore: `${commons.communityURL}/t/fetias101-flow/87`
        },
        Menu: {
          name: "menu",
          fieldName: "menus",
          displayName: "Listing",
          description: "curated list with defined views",
          creatable: true,
          editable: true,
          authorization: true,
          automation: false,
          disabled: !this.isCapableOf(SYSTEM_CAPABILITY.Query) || !this.isCapableOf(SYSTEM_CAPABILITY.Read),
          relyOn: ["information"],
          learnMore: `${commons.communityURL}/t/fetias101-listing/96`
        },
        Document: {
          name: "document",
          fieldName: "documents",
          displayName: "Template",
          description: "documents or reports",
          creatable: true,
          editable: true,
          authorization: true,
          automation: false,
          disabled: false,
          relyOn: ["information"],
          learnMore: `${commons.communityURL}/t/fetias101-template/89`
        },
        Action: {
          name: "action",
          fieldName: "actions",
          displayName: "Custom",
          description: "special actions",
          creatable: true,
          editable: true,
          authorization: true,
          automation: false,
          disabled: false,
          relyOn: ["information"],
          beta: true
        },
        LogicService: {
          name: "logicService",
          fieldName: "logicServices",
          displayName: "Logic",
          description: "extra boost of logics",
          creatable: true,
          editable: true,
          authorization: false,
          automation: false,
          disabled: false,
          relyOn: ["information"],
          beta: true
        }
      }
    },
    cards() {
      const cards = [
        { 
          id: 'information',
          name: 'Information',
          designElements: [this.DesignElement.Information],
          activeKey: ['information', 'informationDefault'],
        },
        { 
          id: 'listing',
          name: 'Listing',
          designElements: [this.DesignElement.Menu],
          activeKey: ['menu'],
        },
        { 
          id: 'activity',
          name: 'Activity',
          designElements: [this.DesignElement.Activity],
          activeKey: ['activity', 'standardActivity'],
        },
        { 
          id: 'workflow',
          name: 'Workflow',
          designElements: [this.DesignElement.State, this.DesignElement.Flow],
          activeKey: ['state', 'flow'],
        },
        { 
          id: 'template',
          name: 'Template',
          designElements: [this.DesignElement.Document],
          activeKey: ['document'],
        }
      ];
      return this.isBeta ?
        [...cards, { 
          id: 'expert',
          name: 'Expert',
          designElements: [this.DesignElement.Action, this.DesignElement.LogicService],
          activeKey: ['action', 'logicService'],
          isBeta: true
        }] :
        cards
      ;
    },
    metaTitle() {
      let title = ['Inistate Builder'];
      this.workspace.name && title.push(this.workspace.name);
      this.module.name && title.push(this.module.name);
      return title.reverse().join(' - ');
    },
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    isBeta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
    isAdminUser() {
      return authorizationStore.getters.isAdminUser;
    },
    appLogo() {
      return authorizationStore.getters.appLogo;
    },
    userProfile() {
      return authorizationStore.getters.userProfile;
    },
    workspace() {
      return this.$store.state.workspace || {};
    },
    workspaceModules() {
      return this.$store.getters.workspaceModules;
    },
    module() {
      return this.$store.state.vector || {};
    },
    moduleAutomation() {
      return this.mutableCanvas.triggers && this.mutableCanvas.triggers.filter((x) => x.type === 'vector' && x.item === null);
    },
    moduleAutomationCount() {
      return Array.isArray(this.moduleAutomation) ? this.moduleAutomation.filter((x) => x.actionType === 'automation').length : 0;
    },
    moduleNotificationCount() {
      return Array.isArray(this.moduleAutomation) ? this.moduleAutomation.filter((x) => x.actionType === 'notification').length : 0;
    },
    builderSyncEnabled() {
      return this.builderSync && (this.module && this.module.published !== null && this.module.published !== undefined ? this.module.published : true);
    },
    mutableCanvas: {
      get() {
        return this.$store.state.canvas;
      },
      set(val) {
        this.$store.dispatch("updateVectorCanvas", val);
      }
    },
    mutableFilterKeyword: {
      get() {
        return this.$store.state.filterKeyword;
      },
      set(val) {
        this.$store.dispatch("filterElement", val ? val.toLowerCase() : null);
      }
    },
    informationSystemDefault() {
      const res = [...canvasDesign.informationSystemDefault];
      return {
        information: this.module && this.module.isUserCollection ? res : res.filter(x => !x.userCollection)
      }
    },
    isModuleContainsMenu() {
      return Boolean(this.mutableCanvas && this.mutableCanvas.menus && this.mutableCanvas.menus.length);
    },
    cardElementsCount() {
      const getFilterCount = (list) => 
        Array.isArray(list) ?
          this.mutableFilterKeyword ?
            list.filter((x) =>
              (x.displayName && x.displayName.toLowerCase().indexOf(this.mutableFilterKeyword) > -1) ||
              (x.name && x.name.toLowerCase().indexOf(this.mutableFilterKeyword) > -1)
            ).length :
            list.length
          : 0
        ;
      return {
        information: getFilterCount(this.mutableCanvas[this.DesignElement.Information.fieldName]),
        informationDefault: getFilterCount(this.informationSystemDefault.information),
        menu: getFilterCount(this.mutableCanvas[this.DesignElement.Menu.fieldName]),
        activity: getFilterCount(this.mutableCanvas[this.DesignElement.Activity.fieldName]),
        standardActivity: getFilterCount(this.mutableCanvas[this.Activity_System_Default.fieldName]),
        state: getFilterCount(this.mutableCanvas[this.DesignElement.State.fieldName]),
        flow: getFilterCount(this.mutableCanvas[this.DesignElement.Flow.fieldName]),
        document: getFilterCount(this.mutableCanvas[this.DesignElement.Document.fieldName]),
        action: getFilterCount(this.mutableCanvas[this.DesignElement.Action.fieldName]),
        logicService: getFilterCount(this.mutableCanvas[this.DesignElement.LogicService.fieldName])
      }
    }
  },
  created() {
    window.appName = 'inistate-builder';
    this.discordURL = commons.discordURL;
  },
  async mounted() {
    // reset classification store
    classificationStore.dispatch("reset");
    //web app config
    await webAppConfig.getConfig();
    // profile
    await authorizationStore.dispatch("profile");
    // workspace
    let workspaceError = null;
    try {
      await this.$store.dispatch("loadWorkspace", { id: $cookies.get('wsId') });
    } catch(err) {
      try {
        const wsId = await this.$store.dispatch("getFirstOrDefaultWorkspaceId");
        await this.$store.dispatch("loadWorkspace", { id: wsId });
      } catch(err) {
        console.error(err);
        workspaceError = true;
      }
    }
    // access guard
    if (!this.isAdminUser) {
      this.$toasted.error('401 Unauthorized access to Builder');
      this.$router.replace({ name: 'dashboard' });
      throw new Error("Unauthorized access to builder. Redirect to dashboard.");
    } else if (workspaceError) {
      this.$toasted.global.error({
        message: 'Unable to load Workspace'
      });
      this.$router.replace({ name: 'dashboard' });
      throw new Error("Unauthorized access to builder. Redirect to dashboard.");
    }
    this.isReady = true;
    // back from Stripe Checkout → open the Plan & Credits tab (not awaited: a modal, not a step)
    this.openBillingReturn();
    window.eventBus.$on("builderSave", this.save);
    window.addEventListener("message", this.getEventMessage);
    this.$nextTick(async () => {
      this.setDeviceUrl("app");
      await this.deviceOnload();
      this.setDeviceUrl("web");
      await this.deviceOnload();
      this.isDeviceLoading = false;
      if (!(this.workspaceModules && this.workspaceModules.length)) {
        this.openModuleCreateModal();
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("message", this.getEventMessage);
    window.eventBus.$off("builderSave", this.save);
    this.$store.dispatch("resetState");
  }
}
</script>

<style lang="scss">
@use "../src/assets/scss/vars.scss" as *;
@use "../src/assets/scss/colors" as *;

body {
  overflow: hidden;
}
.fts-builder {
  height: 100%;
  display: flex;
  overflow: hidden;
}
.builder-loading-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--fts-card-background);
  z-index: $z-index-loading-overlay;
}
.builder-container {
  position: relative;
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.builder-header {
  min-height: 51px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  border-bottom: 1px solid var(--fts-border);
  background-color: var(--fts-card-background);
  transition: transform 0.3s ease-in-out;
  .builder-header__l {
    min-width: 200px;
    flex: 1;
    display: flex;
    align-items: center;
    .builder-header-status {
      display: flex;
      align-items: center;
      margin-left: 1rem;
      .builder-header-status-label {
        margin-left: 0.45rem;
        color: var(--fts-default);
        font-size: 0.75rem;
        cursor: default;
      }
    }
  }
  .builder-header__r {
    flex: 1;
    min-width: 200px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  .builder-header-title {
    margin-left: 1.5rem;
    margin-right: 1.5rem;
    font-size: 1.125rem;
    font-weight: 500;
    color: var(--fts-text);
  }
  .builder-header-workspace {
    display: flex;
    align-items: center;
    font-weight: 500;
    font-size: 1.125rem;
    color: var(--fts-text);
    cursor: pointer;
    .builder-header-workspace-label {
      white-space: nowrap;
      overflow: hidden;
    }
    .builder-header-workspace-caret {
      margin-left: 0.5rem;
      font-size: 0.875rem;
    }
  }
  &.builder-header--hidden {
    position: absolute;
    top: 0;
    width: 100%;
    transform: translateY(-100%);
  }
}
.builder-body {
  height: 100%;
  position: relative;
  display: flex;
  flex-grow: 1;
  overflow: hidden;
}
.builder-drawer-r {
  width: 360px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border-left: 1px solid var(--fts-border);
  background-color: var(--fts-card-background);
  transition: transform 0.3s ease-in-out;
  .builder-drawer__header {
    display: flex;
    align-items: center;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    color: var(--fts-text);
    .builder-drawer-header-btn.fts-btn {
      min-height: 1.75rem;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
      font-weight: 400;
      font-size: 0.75rem;
      > svg {
        margin-right: 0.25rem;
        font-size: 0.75rem;
      }
    }
    .builder-drawer-header-btn-rounded.fts-btn {
      min-height : 28px;
      min-width: 28px;
      height: 28px;
      width: 28px;
      svg {
        font-size: 0.75rem;
      }
    }
    .builder-drawer-header-btn-text-ml {
      margin-left: -0.5rem;
    }
    &.builder-drawer__header-border-b {
      border-bottom: 1px solid var(--fts-border);
    }
    &.builder-drawer__header-module {
      justify-content: space-between;
      cursor: default;
      .builder-drawer-header-action {
        display: flex;
        .builder-drawer-header-action-toggle {
          font-size: 0.875rem;
          &:not(:last-child) {
            margin-right: 0.75rem;
          }
        }
        &.builder-drawer-header-action__l {
          > *:last-child {
            margin-right: 0.5rem;
          }
        }
        &.builder-drawer-header-action__r {
          justify-content: flex-end;
          > *:first-child {
            margin-left: 0.5rem;
          }
        }
      }
      .builder-drawer-header-label {
        flex: 1;
        min-width: 0;
        font-weight: 500;
        &.builder-drawer-header-label--unpublished {
          opacity: 0.5;
        }
      }
      &.builder-drawer__header-module--clickable {
        cursor: pointer;
      }
    }
    &.builder-drawer__header-design {
      border-top: 1px solid var(--fts-border);
      cursor: pointer;
      .builder-drawer-header-label {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        &:not(:last-child) {
          margin-right: 1rem;
        }
      }
      .builder-drawer-header-label-name {
        @include text-overflow-ellipsis;
        font-weight: 500;
      }
      .builder-drawer-header-label-badge {
        margin-left: 0.25rem;
      }
      .builder-drawer-header-label-count {
        flex-shrink: 0;
        margin-left: 0.375rem;
        color: var(--fts-default);
        font-size: 0.75rem;
      }
    }
  }
  .builder-drawer__toggle {
    display: flex;
    align-items: center;
    margin: 0.25rem 0.75rem 0.5rem;
    border: 1px solid var(--fts-primary);
    border-radius: 0.25rem;
    .builder-drawer-toggle {
      flex: 1;
      padding: 0.25rem;
      font-size: 0.75rem;
      color: var(--fts-text);
      text-align: center;
      cursor: pointer;
      &:not(:first-child) {
        border-left: 1px solid var(--fts-primary);
      }
      &.builder-drawer-toggle--active {
        background-color: var(--fts-primary);
        color: var(--fts-light);
      }
    }
  }
  .builder-drawer__content {
    flex-grow: 1;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
  }
  .builder-drawer__action {
    margin: 0.5rem;
  }
  .builder-drawer-module-form {
    flex-grow: 1;
    padding: 0 0.75rem 0.75rem;
  }
  &.builder-drawer-r--hidden {
    position: absolute;
    right: 0;
    height: calc(100% - 51px);
    transform: translateX(100%);
  }
}
.builder-content {
  position: relative;
  height: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  background-color: var(--fts-background);
  .builder-content-control {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.25rem;
    color: var(--fts-text);
    .builder-content-control__l {
      min-width: 150px;
      flex: 1;
      padding: 0.25rem 0.5rem;
      .fts-run-as {
        padding: 0;
      }
    }
    .builder-content-control__c {
      min-width: 0;
      max-width: 425px;
      flex: 1 1 425px;
      padding-top: 0.25rem;
      padding-bottom: 0.25rem;
    }
    .builder-content-control__r {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 0.25rem 0.5rem;
      > *:not(:first-child) {
        margin-left: 0.25rem;
      }
    }
    .builder-content-control-url {
      padding: 0.375rem 1rem;
      border: 1px solid var(--fts-border);
      border-radius: 10em;
      background-color: var(--fts-card-background);
      font-size: 0.875rem;
      line-height: 1.5;
      white-space: nowrap;
      overflow: hidden;
    }
    &.builder-content-control--fab {
      position: absolute;
      left: 0;
      right: 0;
      pointer-events: none;
      .builder-content-control__l {
        border-radius: 0.5rem;
        pointer-events: auto;
      }
      .builder-content-control__r {
        border-radius: 0.5rem;
        pointer-events: auto;
      }
      & + .builder-content-playground {
        padding-top: 1rem;
      }
    }
  }
  .builder-content-playground {
    flex-grow: 1;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    overflow: hidden;
  }
  .builder-content-dashboard {
    padding-top: 1rem;
  }
  .builder-content-notification-panel {
    padding: 1rem;
  }
}
.design-selection {
  display: flex;
  flex-wrap: wrap;
  .design-selection-option {
    flex: 1;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--fts-border);
    border-right: 1px solid var(--fts-border);
    text-align: center;
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-text);
    cursor: pointer;
    &.design-selection-option--active {
      color: var(--fts-primary);
    }
  }
}
.builder-ai-intention {
  margin-right: 0.5rem;
  > i {
    font-size: 1rem;
  }
}
.builder-filter {
  flex: 1;
  .builder-filter-input {
    border: 0;
    border-radius: 0.375rem;
    @include theme {
      background-color: rgba(theme-get('fts-gray-100'), 0.75);
    }
    &:focus {
      box-shadow: none;
    }
  }
}
.fts-builder {
  position: relative;
  .client-sidenav {
    border-right: 1px solid var(--fts-border);
    background-color: var(--fts-card-background);
    transition: transform 0.3s ease-in-out;
    .sidenav-menu {
      &.apex {
        .sidenav-menu-wrapper {
          .sidenav-icon {
            color: var(--fts-secondary);
          }
          &:hover {
            background-color: var(--fts-secondary);
            .sidenav-icon {
              color: var(--fts-light);
            }
          }
        }
      }
      .sidenav-menu-wrapper {
        .sidenav-icon {
          color: var(--fts-default);
        }
        .sidenav-workspace {
          background-color: var(--fts-default);
          color: var(--fts-light);
        }
        &:hover {
          background-color: var(--fts-border);
          .sidenav-icon {
            color: var(--fts-dark);
          }
        }
        &.active {
          @include theme {
            background-color: rgba(theme-get('fts-primary'), 0.125);
          }
          .sidenav-icon {
            color: var(--fts-primary);
          }
          .sidenav-workspace {
            background-color: var(--fts-primary);
            color: var(--fts-light);
          }
        }
      }
    }
    &.client-sidenav--hidden {
      position: absolute;
      left: 0;
      height: 100%;
      transform: translateX(-100%);
    }
  }
}
</style>